import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout, { useViewport, TopBar } from '../../components/Layout';
import { Meta, Display } from '../../components/Atoms';

/**
 * ProfileShell - wspólny layout dla wszystkich sub-routes profilu.
 *
 * Mobile: cały sub-route na pełnej szerokości z back-link na górze.
 * Desktop: sidebar z 5 sekcjami (active highlight) + main z treścią.
 */

const SECTIONS = [
  { key: 'hub',     to: '/profile',         label: 'Profil',          short: 'Profil' },
  { key: 'garden',  to: '/profile/garden',  label: 'Mój ogród',       short: 'Ogród' },
  { key: 'public',  to: '/profile/public',  label: 'Profil publiczny', short: 'Pub.' },
  { key: 'account', to: '/profile/account', label: 'Konto',           short: 'Konto' },
  { key: 'stats',   to: '/profile/stats',   label: 'Statystyki',       short: 'Stat.' },
];

function activeKey(pathname) {
  if (pathname === '/profile' || pathname === '/profile/') return 'hub';
  for (const s of SECTIONS) {
    if (s.key !== 'hub' && pathname.startsWith(s.to)) return s.key;
  }
  return 'hub';
}

function ProfileSidebar({ active }) {
  return (
    <aside style={{
      width: 240,
      flexShrink: 0,
      borderRight: '1px solid var(--t-line)',
      padding: '32px 0',
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      maxHeight: '100vh',
      overflowY: 'auto',
    }}>
      <div style={{ padding: '0 24px 24px' }}>
        <Meta>Profil</Meta>
      </div>
      <nav style={{ padding: '0 12px' }}>
        {SECTIONS.map(s => {
          const on = s.key === active;
          return (
            <Link
              key={s.key}
              to={s.to}
              style={{
                display: 'block', padding: '12px 14px',
                borderRadius: 9, marginBottom: 2,
                background: on ? 'rgba(168, 196, 158, 0.10)' : 'transparent',
                color: on ? 'var(--t-text)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-sans)', fontSize: 14,
                fontWeight: on ? 500 : 400,
                letterSpacing: '-.01em',
                textDecoration: 'none',
                transition: 'background 200ms ease',
              }}
            >{s.label}</Link>
          );
        })}
      </nav>
    </aside>
  );
}

export default function ProfileShell({ children, title, subtitle, eyebrow }) {
  const location = useLocation();
  const active = activeKey(location.pathname);
  const { isPhone, isTablet } = useViewport();

  if (isPhone || isTablet) {
    return (
      <Layout>
        <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 20 }}>
          {active !== 'hub' && (
            <div style={{ padding: '0 20px 16px' }}>
              <Link to="/profile" style={{
                fontFamily: 'var(--t-mono)', fontSize: 11,
                letterSpacing: '.18em', textTransform: 'uppercase',
                color: 'var(--t-sage)', textDecoration: 'none',
              }}>← Profil</Link>
            </div>
          )}
          <div style={{ padding: '0 24px 24px' }}>
            {eyebrow && <Meta color="sage" style={{ marginBottom: 8 }}>{eyebrow}</Meta>}
            {title && (
              <Display size={'clamp(36px, 8vw, 48px)'} style={{
                display: 'block', letterSpacing: '-.025em', lineHeight: 0.95,
                marginBottom: subtitle ? 12 : 24,
              }}>{title}</Display>
            )}
            {subtitle && (
              <div style={{
                fontSize: 14, color: 'var(--t-text-dim)',
                lineHeight: 1.55, marginBottom: 24,
              }}>{subtitle}</div>
            )}
          </div>
          <div style={{ padding: '0 24px 60px' }}>{children}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout topBar={<TopBar
      left={<Meta>Profil · {SECTIONS.find(s => s.key === active)?.label || ''}</Meta>}
    />}>
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <ProfileSidebar active={active} />
        <div style={{ flex: 1, padding: '40px 48px 60px', maxWidth: 760 }}>
          {eyebrow && <Meta color="sage" style={{ marginBottom: 10 }}>{eyebrow}</Meta>}
          {title && (
            <Display size={48} style={{
              display: 'block', letterSpacing: '-.03em',
              marginBottom: subtitle ? 14 : 32,
            }}>{title}</Display>
          )}
          {subtitle && (
            <div style={{
              fontSize: 15, color: 'var(--t-text-dim)',
              lineHeight: 1.55, marginBottom: 32, maxWidth: 520,
            }}>{subtitle}</div>
          )}
          {children}
        </div>
      </div>
    </Layout>
  );
}

// Reusable input style identyczny z auth
export const profileInputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: 12,
  background: 'var(--t-bg)',
  border: '1px solid var(--t-line-2)',
  color: 'var(--t-text)',
  fontFamily: 'var(--t-sans)',
  fontSize: 16,
  letterSpacing: '-.01em',
  outline: 'none',
  boxSizing: 'border-box',
};

export function ProfileLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} style={{
      display: 'block', marginBottom: 8,
      fontFamily: 'var(--t-mono)', fontSize: 10,
      letterSpacing: '.22em', textTransform: 'uppercase',
      color: 'var(--t-text-dim)',
    }}>{children}</label>
  );
}

export function ProfileSection({ title, description, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{
        paddingBottom: 12, marginBottom: 18,
        borderBottom: '1px solid var(--t-line-2)',
      }}>
        <Display size={20} style={{ display: 'block', marginBottom: description ? 6 : 0 }}>
          {title}
        </Display>
        {description && (
          <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>
            {description}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
